import React, { ReactElement, useContext } from "react";
import { Box } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

type Debit = {
  data: string;
  description: string;
};

type Credit = {
  data: string;
  description: string;
};

type RowData = {
  id: string;
  created_at: string;
  record_owner: string;
  type: string;
  debit: Debit;
  credit: Credit;
  asset: string;
  status: string;
};

const customDataGridStyles: Partial<GridClasses> = {
  root: "mui-table-custom",
  columnHeader: "text-start text-md",
  row: "",
};

const TransactionList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel
}) => {
    const { profileTimeZone } = useContext(ProfileContext);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    interface RecordStatus {
      status: string;
      value: string;
      display: ReactElement;
    }

    const statusClasses = "status";

    const statusList: RecordStatus[] = [
      {
        status: "pending",
        value: "pending",
        display: (
          <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
        ),
      },
      {
        status: "complete",
        value: "complete",
        display: (
          <Badge className={`${statusClasses} status-successful`}>
            Completed
          </Badge>
        ),
      },
      {
        status: "cancelled",
        value: "cancelled",
        display: (
          <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
        ),
      },
      {
        status: "failed",
        value: "failed",
        display: (
          <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
        ),
      },
    ];

    const handleClickCopy = async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
      } catch (err) {
        console.error("Failed to copy ID: ", err);
      }
    };

    const columns: GridColDef[] = [
      {
        field: "id",
        headerName: "",
        flex: 1,
        minWidth: 50,
        maxWidth: 50,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
          <Tooltip title="Copy ID" arrow placement="top">
            <div>
              <IconButton
                id={"button-copy-id-" + params?.row?.id}
                onClick={() => handleClickCopy(params.row.id)}
              >
                <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
              </IconButton>
            </div>
          </Tooltip>
        ),
      },
      {
        field: "created_at",
        headerName: "Created Date",
        minWidth: 180,
        sortable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<RowData, string>) =>
          formatDate(params.row.created_at),
      },
      {
        field: "record_owner",
        headerName: "",
        minWidth: 180,
        sortable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
          params.row.record_owner,
      },
      {
        field: "type",
        headerName: "Type",
        minWidth: 180,
        sortable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
          userRole === "agent"
            ? String(params.row.type).replace("Agent", "")
            : params.row.type,
      },
      {
        field: "debit",
        headerName: "Debit",
        minWidth: 320,
        sortable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
          <div>
            <div>{params.row.debit.data ? params.row.debit.data : ""}</div>
            <div style={{ fontSize: "0.85rem", color: "gray" }}>
              {params.row.debit.description ? params.row.debit.description : ""}
            </div>
          </div>
        ),
      },
      {
        field: "credit",
        headerName: "Credit",
        minWidth: 320,
        sortable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
          <div>
            <div>{params.row.credit.data ? params.row.credit.data : ""}</div>
            <div style={{ fontSize: "0.85rem", color: "gray" }}>
              {params.row.credit.description ? params.row.credit.description : ""}
            </div>
          </div>
        ),
      },
      {
        field: "asset",
        headerName: "Asset",
        minWidth: 80,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
          params.row.asset,
      },
      {
        field: "status",
        headerName: "",
        minWidth: 200,
        sortable: false,
        flex: 1,
        headerAlign: "right",
        align: "right",
        renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
          const status = params.value as string;
          const statusItem = statusList.find((item) => item.status === status);
          return statusItem ? statusItem.display : null;
        },
      },
    ];

    const formatDate = (timestamp: string) => {
      return dayjs(Number(timestamp))
        .tz(profileTimeZone)
        .format("DD MMM YY - hh:mm A");
    };

    const filterColumns = () => {
      let rebuildColumns = [];
      if (userRole !== "superadmin" && userRole !== "agent" && userRole !== "merchant") {
        const excludeFields = ["record_owner"];
        rebuildColumns = columns.filter(
          (column) => !!!excludeFields.includes(column?.field)
        );
      } else {
        rebuildColumns = columns;
      }
      return rebuildColumns;
    };

    return (
      <>
        <Box className="card w-full">
          <DataGrid
            sx={{
              "& .MuiDataGrid-cell": {
                padding: "8px 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "8px 16px",
              },
              "& .MuiTablePagination-input": {
                marginRight: "-20px",
              },
              "& .MuiTablePagination-displayedRows": {
                display: "none",
              },
            }}
            loading={isLoading}
            rows={tableData || []}
            columns={filterColumns()}
            getRowId={(row) => row.id}
            rowCount={rowCount}
            sortingMode="server"
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            paginationModel={{ page: page, pageSize: pageSize }}
            onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={page === 0 && rowCount < pageSize}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      </>
    );
  };

export default TransactionList;
